import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

/**
 * The consent screen of the OAuth flow (Claude.ai connectors). A Claude app sent the person here; they
 * are signed in (RequireAuth brought them through the phone code if not) and decide. Allow asks the API
 * for a one-time code and goes back to the app; Deny goes back with an error.
 */
export function OAuthAuthorizePage() {
  const { t } = useLocale();
  const { me } = useAuth();
  const [params] = useSearchParams();
  const clientId = params.get('client_id') ?? '';
  const redirectUri = params.get('redirect_uri') ?? '';
  const state = params.get('state');
  const codeChallenge = params.get('code_challenge') ?? '';
  const method = params.get('code_challenge_method');
  const resource = params.get('resource');
  const scope = params.get('scope');
  const valid = clientId !== '' && redirectUri !== '' && codeChallenge.length >= 43 && method === 'S256';
  const client = useQuery({ queryKey: qk.oauthClient(clientId), queryFn: ({ signal }) => api.oauthClient(clientId, signal), enabled: valid });
  const approve = useMutation({
    mutationFn: () =>
      api.oauthApprove({
        clientId,
        redirectUri,
        codeChallenge,
        codeChallengeMethod: 'S256',
        ...(state !== null ? { state } : {}),
        ...(resource ? { resource } : {}),
        ...(scope ? { scope } : {}),
      }),
    onSuccess: (r) => window.location.assign(r.redirectTo),
  });
  // Deny goes back only to an address this client registered, and only over https (or a local address): anything
  // else (a javascript: link, another site) goes home instead
  const deny = () => {
    try {
      const to = new URL(redirectUri);
      const local = to.protocol === 'http:' && (to.hostname === 'localhost' || to.hostname === '127.0.0.1');
      if (!client.data?.redirectUris.includes(redirectUri) || !(to.protocol === 'https:' || local)) throw new Error('not registered');
      to.searchParams.set('error', 'access_denied');
      if (state !== null) to.searchParams.set('state', state);
      window.location.assign(to.toString());
    } catch {
      window.location.assign('/');
    }
  };

  return (
    <div className="stack">
      <PageHead title={t('oauthTitle')} back="/" />
      {!valid ? (
        <Empty>{t('oauthBad')}</Empty>
      ) : (
        <QueryView query={client}>
          {(c) => (
            <section className="card stack-sm">
              <h2>{t('oauthAsk').replace('{app}', c.clientName)}</h2>
              <p>
                <strong>{me?.name}</strong> · {me?.role}
              </p>
              <p className="muted">{t('oauthHint')}</p>
              <p className="muted">{new URL(redirectUri).host}</p>
              {approve.error ? <ErrorBox error={approve.error} /> : null}
              <button type="button" className="btn btn-primary btn-lg btn-block" onClick={() => approve.mutate()} disabled={approve.isPending}>
                {t('allow')}
              </button>
              <button type="button" className="btn btn-ghost btn-block" onClick={deny} disabled={approve.isPending}>
                {t('deny')}
              </button>
            </section>
          )}
        </QueryView>
      )}
    </div>
  );
}
